import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Instagram, ExternalLink, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const Portfolio: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const location = useLocation();
  const { config } = useSiteConfig();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      // Restore scroll to top since typically navigations keep previous scroll position
      window.scrollTo({ top: 0, behavior: 'instant' });
      // Small timeout to ensure the tab gets selected smoothly if there's any mount delay
      setTimeout(() => setSelectedCategory(categoryParam), 10);
    }
  }, [location.search]);

  const categories = [
    { id: 'all', name: t('portfolio.categories.all') || 'All' },
    { id: 'wedding', name: t('portfolio.categories.wedding') || 'Wedding' },
    { id: 'birthday', name: t('portfolio.categories.birthday') || 'Birthday' },
    { id: 'cupcakes', name: t('portfolio.categories.cupcakes') || 'Cupcakes' },
    { id: 'corporate', name: t('portfolio.categories.corporate') || 'Corporate' },
    { id: 'baby', name: t('portfolio.categories.baby') || 'Baby Shower' },
    { id: 'celebration', name: t('portfolio.categories.celebration') || 'Celebration' }
  ];

  // Dynamically loaded from /public/tncakes/[Category] via site configuration context
  const portfolioItems = config.galleryItems;

  const filteredItems = selectedCategory === 'all'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === selectedCategory);

  const toggleProductSelection = (product: any) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.id === product.id);
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const isProductSelected = (productId: string) => {
    return selectedProducts.some(p => p.id === productId);
  };

  const handleCheckout = () => {
    // Store selected products in localStorage to pass to inquiry page
    localStorage.setItem('selectedProducts', JSON.stringify(selectedProducts));
    // Navigate to inquiry page
    window.location.href = '/inquiry';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <section className="pt-20 md:pt-24 pb-12 md:pb-16 bg-gradient-to-r from-red-50 via-pink-50 to-red-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-900 mb-4 md:mb-6">
              {t('portfolio.title')}
            </h1>
            <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-red-400 to-pink-500 rounded-full mx-auto mb-4 md:mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-6 md:mb-8 px-4">
              {t('portfolio.subtitle')}
            </p>
            <a
              href="https://www.instagram.com/tncakesbali"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-4 border-2 border-red-400 text-red-500 font-semibold rounded-full transition-all duration-300 hover:bg-red-400 hover:text-white hover:scale-105"
            >
              <Instagram className="w-5 h-5" />
              {t('portfolio.followInstagram')}
              <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Filter Categories */}
      <section className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center gap-3 md:gap-4 mb-8 md:mb-12"
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-medium text-sm md:text-base transition-all duration-300 ${selectedCategory === category.id
                  ? 'bg-gradient-to-r from-red-400 to-pink-500 text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-600 hover:scale-105'
                  }`}
              >
                {category.name}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-8 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                {/* Image */}
                <div className="relative h-64 md:h-80 overflow-hidden rounded-t-2xl group">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                  {/* Selection Checkbox */}
                  <div className="absolute top-4 left-4 z-50">
                    <button
                      onClick={() => toggleProductSelection(item)}
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${isProductSelected(item.id)
                        ? 'bg-red-500 border-red-500 text-white'
                        : 'bg-white/20 border-white/50 text-white hover:bg-white/30'
                        }`}
                    >
                      {isProductSelected(item.id) && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Product Code */}
                  <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-semibold">
                    {item.code}
                  </div>

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-red-500/90 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                    <button
                      onClick={() => setSelectedItem(item)}
                      className="bg-white text-red-500 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 transform scale-90 group-hover:scale-100"
                    >
                      {t('portfolio.viewDetails')}
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-serif font-semibold text-gray-900 mb-2 md:mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base mb-4 leading-relaxed">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-red-500 font-semibold text-sm">
                      {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                    </span>
                    <button
                      onClick={() => setSelectedItem(item)}
                      className="text-red-500 hover:text-red-600 font-medium text-sm transition-colors"
                    >
                      {t('portfolio.learnMore')} →
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Floating Checkout Button */}
      <AnimatePresence>
        {selectedProducts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-24 right-6 z-50"
          >
            <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="bg-white/20 text-white px-2 py-1 rounded-full text-sm font-semibold">
                  {selectedProducts.length}
                </span>
                <span className="text-sm font-medium">
                  {selectedProducts.length === 1 ? 'item selected' : 'items selected'}
                </span>
              </div>
              <button
                onClick={handleCheckout}
                className="bg-white text-red-500 px-6 py-2 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300"
              >
                Checkout →
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detailed Modal */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 portfolio-modal"
            onClick={() => setSelectedItem(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative">
                <div className="h-[50vh] md:h-[60vh] bg-black rounded-t-2xl overflow-hidden flex items-center justify-center">
                  <img
                    src={selectedItem.image}
                    alt={selectedItem.title}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 rounded-t-2xl pointer-events-none"></div>

                <button
                  onClick={() => setSelectedItem(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-800 hover:bg-white transition-all duration-300 z-10 shadow-lg"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="absolute bottom-6 left-6 right-6 text-white z-10">
                  <div className="bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-sm font-semibold inline-block mb-2">
                    {selectedItem.code}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-serif font-bold mb-2 drop-shadow-lg">
                    {selectedItem.title}
                  </h2>
                  <span className="px-4 py-1.5 bg-red-500 text-white text-sm rounded-full font-medium shadow-lg">
                    {selectedItem.category.charAt(0).toUpperCase() + selectedItem.category.slice(1)}
                  </span>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 md:p-8">
                {/* Description */}
                <div className="mb-8">
                  {/* No detailed description */}
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Side - Features */}
                  {/* No features section */}

                  {/* Right Side - Details */}
                  <div className="space-y-6">
                    {/* Occasions */}
                    {/* No occasions section */}

                    {/* CTA Section */}
                    <div className="bg-gradient-to-br from-gold-50 to-yellow-50 p-6 rounded-xl border border-gold-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                        {t('portfolio.modal.interestedTitle')}
                      </h4>
                      <div className="space-y-3">
                        <button className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white py-3 rounded-full font-semibold hover:from-red-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl">
                          {t('portfolio.modal.startOrder')}
                        </button>
                        <button className="w-full border-2 border-red-400 text-red-500 py-3 rounded-full font-semibold hover:bg-red-50 transition-all duration-300">
                          {t('portfolio.modal.whatsappUs')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Call to Action */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-red-400 to-pink-500">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white mb-4 md:mb-6">
              {t('globalCTA.title')}
            </h2>
            <p className="text-lg md:text-xl text-red-100 max-w-3xl mx-auto mb-6 md:mb-8 leading-relaxed px-4">
              {t('globalCTA.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <a
                href="/inquiry"
                className="bg-white text-red-500 px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {t('globalCTA.orderNow')}
              </a>
              <a
                href="/contact"
                className="border-2 border-white text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg hover:bg-white hover:text-red-500 transition-all duration-300"
              >
                {t('globalCTA.contactUs')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
